<template>
  <div class="row mb-2 g-2">
    <label :for="id" class="col-sm-3 mt-sm-3 col-form-label">
      {{ t(field) }}
    </label>
    <div class="col-sm-8 col-10">
      <slot
        :id
        :invalid="validation.invalid"
        :touch="validation.touch"
        :disabled="disabled || (lockable && !unlocked)"
      />
      <div v-if="validation.error" class="invalid-feedback">
        {{ validation.error }}
      </div>
    </div>
    <div v-if="lockable && !unlocked" class="col-sm-1 col-2">
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="unlocked = true"
      >
        <font-awesome-icon icon="pencil-alt" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ValidationRuleCollection } from "@vuelidate/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { RowFieldKey } from "./fieldKeys";
import { useFieldValidation } from "./useFieldValidation";

/**
 * Label, validation message and optional edit lock around one input of a signup review row. The
 * input goes in the default slot, which receives `id`, `invalid`, `touch` and `disabled`.
 */
const props = withDefaults(
  defineProps<{
    field: RowFieldKey;
    value?: unknown;
    rules?: ValidationRuleCollection;
    disabled?: boolean;
    /** Starts read-only behind a pencil button that unlocks it. */
    lockable?: boolean;
  }>(),
  { value: undefined, rules: () => ({}), disabled: false, lockable: false }
);

const { t } = useI18n();
const unlocked = ref(false);

// `company.bank_name` → `company-bank-name`
const id = computed(() => props.field.replace(/[._]/g, "-"));

const validation = useFieldValidation(
  () => props.value,
  () => props.rules
);
</script>

<style scoped lang="scss">
.col-form-label {
  text-align: left;
  font-weight: bold;
  padding: 0;
}
.invalid-feedback {
  display: block;
}
.row {
  margin-left: 0;
  margin-right: 0;
}

@media (max-width: 575.98px) {
  .col-form-label {
    margin-top: 1rem;
  }
}
</style>
